import { OptionalDatePicker } from "components/DatePicker/OptionalDatePicker";
import { FormSection } from "components/MainSectionTemplate/types";
import PercentageInput from "components/PercentageInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { YesNoRadioGroupForInPageEditing } from "components/ValidatedFields/YesNoRadioGroup";
import moment from "moment";
import React from "react";
import { formatShortDate } from "utilities";
import { getPercentageFormatter } from "utilities/formatter";
import { isNullOrEmptyString } from "shared/utils";

interface Props {
  clientId: number;
  discountPercentage: number;
  discountUntil: moment.Moment;
  discountNote: string;
}

export const discountSection = ({ clientId, discountNote, discountPercentage, discountUntil }: Props): FormSection => {
  const percentageFormatter = getPercentageFormatter(5);

  return {
    key: "discountDetails",
    type: "regular",
    title: "Discount",
    fields: [
      {
        key: "discountYesOrNo",
        label: RESX.Subscription.Discount,
        render: () => {
          return <div>{discountPercentage > 0 ? RESX.GeneralLabels.Yes : RESX.GeneralLabels.No}</div>
        },
        inPageEdit: {
          initialValue: discountPercentage > 0,
          render: ({ value, onChange }) => <YesNoRadioGroupForInPageEditing value={value} onChange={(v) => onChange(v, true)} />,
          type: "custom",
          submitChange: async (value) => value
            ? window.CoreApi.Client.UpdateClientDiscount({ clientId, percentage: discountPercentage || 0.1, note: discountNote, until: discountUntil?.toDate() })
            : window.CoreApi.Client.UpdateClientDiscount({ clientId, percentage: 0, note: "", until: null })
        }
      },
      {
        key: "discountPercentage",
        label: "Percentage",
        render: () => <div>{percentageFormatter.format(discountPercentage)}</div>,
        hidden: discountPercentage === 0,
        inPageEdit: {
          initialValue: discountPercentage,
          render: ({ value, onChange, cancel }) => <PercentageInput onEnter={v => onChange(v, true)} onEsc={cancel} canBeNegative={false} digitsAfterDecimalPoint={2} enablePercentageLabel value={value} onChange={onChange} />,
          type: "confirmOrCancelButton",
          submitChange: async (value) => window.CoreApi.Client.UpdateClientDiscount({ clientId, percentage: value, note: discountNote, until: discountUntil?.toDate() })
        }
      }, {
        key: "discountUntil",
        label: "Until",
        render: () => <div>{discountUntil ? formatShortDate(discountUntil) : "Perpetual"}</div>,
        hidden: discountPercentage === 0,
        inPageEdit: {
          initialValue: discountUntil,
          render: ({ value, onChange }) => <OptionalDatePicker validatorKey="discountUntil" value={value} onChange={onChange} minDate={moment.utc(serverReference.constants.appointment.minDate)} maxDate={moment.utc(serverReference.constants.appointment.maxDate)} />,
          type: "confirmOrCancelButtonInline",
          submitChange: async (value) => window.CoreApi.Client.UpdateClientDiscount({ clientId, percentage: discountPercentage, note: discountNote, until: value?.toDate() })
        }
      }, {
        key: "discountNote",
        label: "Note",
        render: () => <div>{discountNote}</div>,
        hidden: discountPercentage === 0,
        isEmpty: isNullOrEmptyString(discountNote),
        inPageEdit: {
          initialValue: discountNote,
          fullWidth: true,
          render: ({ value, onChange, submit, cancel }) => <OptionalTextInput onEnter={submit} onEsc={cancel} onChange={onChange} value={value} maxLength={255} />,
          type: "confirmOrCancelButtonInline",
          submitChange: async (value) => window.CoreApi.Client.UpdateClientDiscount({ clientId, percentage: discountPercentage, note: value, until: discountUntil?.toDate() })
        }
      }
    ]
  }
}